import React from 'react';

import type { DayPair, HomePeriod } from '../../mock-data/types';
import { customDays, homeWindow, isHomeInterval, periodParams } from '../home/stats-mapping';
import { deltaPct } from '../home/stats-shape';
import { analyticsConfigured, runAggregate } from './api';
import { postTrafficQuality, QualityError } from './quality/api';
import { homeRequest } from './quality/compile';
import { qualityScreenState } from './quality/errors';
import { enqueueQuality } from './quality/queue';
import type { QualityHome } from './quality/types';
import {
  dayVisitors,
  sumTotals,
  totalsByHostQuery,
  visitorsByDayQuery,
  websiteWindow,
  type DayVisitors,
  type Span,
  type WebsiteInterval,
} from './queries';
import { brandHosts } from './useLandingPageViews';

/**
 * All the brand's website visitors (its site and landing pages) over a Home period and the equal one before it, as the
 * Website analytics tab counts them (Pulsar visitors per address, summed), for Home's Acquisition card.
 */
export type WebsiteVisitors = {
  /**
   * `unavailable`: the analytics service is not set up here, or the brand has no tracked address (the card leaves the
   * figure out). `error`: the first read of this period failed (the card shows its failed state); a failed re-read
   * keeps the last good figure.
   */
  status: 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';
  visitors: number;
  /** Change against the period before, in percent (Home's delta). */
  delta: number;
  /** Visitors per day over Home's own days (its points), for Home's chart line (null: not read, so no line); not the
   *  span of `visitors`, so they need not add up to it. Read only where Home charts by the day: daily visitors do not
   *  add up into its months (90D, 6M, 1Y). */
  days: DayVisitors[] | null;
  /** Today / Yesterday: that day's visitors and the whole day before's, for Home's day bars (null on other ranges). */
  day: DayPair | null;
  /** Quality mode only (null otherwise): what the figure counts. `internal-default`: the brand has no public address, so
   *  its internal ones; `no-default-host`: nothing to count, so the figure is a dash with no delta and no line. */
  homeState: 'public' | 'internal-default' | 'no-default-host' | null;
  /** Said in place of the comparison (an English key), kept with the figure it was shown with; null: the comparison. */
  caption: string | null;
};

type Figures = Omit<WebsiteVisitors, 'status'>;

const NONE: Figures = { visitors: 0, delta: 0, days: null, day: null, homeState: null, caption: null };

const DAY_MS = 24 * 60 * 60 * 1000;

/** What a quality answer shows on Home. `daily`: Home charts by the day, so the series draws its line. Pure. */
const qualityFigures = (answer: QualityHome, daily: boolean): Figures => {
  const { scope, hostCatalog, home } = answer;
  if (scope.selectedHosts.length === 0 && hostCatalog.defaultBasis === 'none') {
    // Nothing to count: never a 0, a dash with what is missing said in place of the comparison.
    return {
      ...NONE,
      homeState: 'no-default-host',
      caption: hostCatalog.status === 'unavailable' ? 'Not available right now' : 'No public address yet',
    };
  }
  const now = home.totals.current.people;
  const before = home.totals.previous.people;
  // Only an empty selection has no count: one that counts hosts and answers none is a failed read, never a 0.
  if (now === null || before === null) throw new Error('Home visitors answered no count for a selection');
  const internal = hostCatalog.defaultBasis === 'internal';
  return {
    visitors: now,
    delta: Math.round(deltaPct(now, before)),
    days:
      daily && home.series ? home.series.points.map((p) => ({ at: Date.parse(p.start), visitors: p.people })) : null,
    day: home.dayBars ? { prev: home.dayBars.previous.people, current: home.dayBars.current.people } : null,
    homeState: internal ? 'internal-default' : 'public',
    caption: internal ? 'Internal addresses only' : null,
  };
};

/** The period and the one before it. The Website tab's windows, plus Home's longer ranges and custom runs of days
 *  (whole UTC days; a custom run is read from its own dates, never the Website tab's 90-day one). Pure. */
export const visitSpans = (interval: HomePeriod, nowMs: number): { current: Span; previous: Span } => {
  if (!isHomeInterval(interval)) {
    const range = customDays(interval);
    if (range) {
      const first = Date.parse(`${range.from}T00:00:00Z`);
      const current = { from: first, to: Math.min(Date.parse(`${range.to}T00:00:00Z`) + DAY_MS, nowMs) };
      // The same stretch before it: a range that runs into today compares with as much of the earlier one.
      return { current, previous: { from: first - range.days * DAY_MS, to: current.to - range.days * DAY_MS } };
    }
  }
  if (interval === '6M' || interval === '1Y') {
    const days = interval === '6M' ? 182 : 365;
    const d = new Date(nowMs);
    const day0 = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const current = { from: day0 - (days - 1) * DAY_MS, to: nowMs };
    return { current, previous: { from: current.from - days * DAY_MS, to: nowMs - days * DAY_MS } };
  }
  const win = websiteWindow(interval as WebsiteInterval, nowMs);
  return { current: win.current, previous: win.previous };
};

/**
 * The brand's website visitors for a Home period; nothing is asked while `enabled` is false. Only the newest read lands.
 * `quality`: the brand reads the quality route, and no legacy total is shown.
 */
export const useWebsiteVisitors = (
  tenantId: number | undefined,
  interval: HomePeriod,
  enabled: boolean,
  quality = false,
): WebsiteVisitors => {
  const reads = Boolean(enabled && tenantId);
  // The brand and mode the figures belong to: held figures and the brand left out are per mode, so a mode flip
  // reuses nothing of the other mode.
  const brand = `${tenantId}:${quality ? 'quality:person:unflagged' : 'legacy'}`;
  // Starts loading when it will read, so the card reserves the figure's slot from its first paint. `brand` and
  // `period`: whose figures these are (another brand's, mode's or period's are never shown).
  const [state, setState] = React.useState<WebsiteVisitors & { brand: string; period: HomePeriod }>(() => ({
    brand,
    period: interval,
    status: reads && analyticsConfigured() ? 'loading' : 'idle',
    ...NONE,
  }));
  const readId = React.useRef(0);
  /** The brand known to have no analytics here (not set up, or no tracked address): its figure stays out across
   *  period changes instead of showing a skeleton that collapses again. */
  const outFor = React.useRef<string | undefined>(undefined);
  // The last good figures per brand, mode, period and window, kept when a re-read of them fails.
  const held = React.useRef(new Map<string, Figures>());

  // Syncs with the analytics service: this period's visitors and the period before's.
  React.useEffect(() => {
    readId.current += 1;
    const id = readId.current;
    if (!enabled || !tenantId) {
      setState({ brand, period: interval, status: 'idle', ...NONE });
      return undefined;
    }
    if (!analyticsConfigured()) {
      setState({ brand, period: interval, status: 'unavailable', ...NONE });
      return undefined;
    }
    const nowMs = Date.now();
    const { current, previous } = visitSpans(interval, nowMs);
    const daily = periodParams(interval, nowMs).intervalSlice === '1 day';
    // Held per resolved window too: the figure's first UTC day (past midnight, Today is another day, and a held figure
    // of the day before never stands for it) and, where a line is drawn, its first date, the viewer's own.
    const key = `${brand}:${interval}:${current.from}${daily ? `:${homeWindow(interval, nowMs).startDate}` : ''}`;
    const controller = new AbortController();
    const live = (): boolean => id === readId.current && !controller.signal.aborted;
    if (outFor.current !== brand)
      setState((s) =>
        s.brand === brand && s.period === interval
          ? { ...s, status: 'loading' }
          : { brand, period: interval, status: 'loading', ...NONE },
      );
    // A failed read is passing (busy after the retry, a query refused, a network failure): a re-read keeps the last
    // good figure with the state and caption it was shown with, a first read shows the failed state.
    const failed = (): void => {
      if (!live()) return;
      const last = held.current.get(key);
      setState(
        last
          ? { brand, period: interval, status: 'ready', ...last }
          : { brand, period: interval, status: 'error', ...NONE },
      );
    };
    if (quality) {
      // Nothing to count here (no address, or the route not set up yet): the figure stays out, never a failed state.
      const leaveOut = (): void => {
        if (!live()) return;
        outFor.current = brand;
        setState({ brand, period: interval, status: 'unavailable', ...NONE });
      };
      (async (): Promise<void> => {
        const answer = await enqueueQuality(
          tenantId,
          'home',
          () => postTrafficQuality(tenantId, homeRequest(interval, nowMs), controller.signal),
          controller.signal,
        );
        if (!('home' in answer)) {
          leaveOut();
          return;
        }
        outFor.current = undefined;
        const figures = qualityFigures(answer, daily);
        held.current.set(key, figures);
        if (live()) setState({ brand, period: interval, status: 'ready', ...figures });
      })().catch((err: unknown) => {
        if (err instanceof QualityError && qualityScreenState(err.code).kind === 'not-set-up') leaveOut();
        else failed();
      });
      return () => controller.abort();
    }
    const totalsIn = async (span: Span): Promise<number> =>
      sumTotals((await runAggregate(tenantId, totalsByHostQuery(span), controller.signal)).rows).visitors;
    // The day bars (Today / Yesterday) compare with the whole day before, so the daily read starts a day earlier there.
    const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
    // Elsewhere it reads Home's own days, so each lands on its point: the multi-day ranges chart the viewer's calendar
    // dates (each point starts at that date's UTC midnight), which run a day off the UTC days for much of the day.
    const homeDays = (): Span => {
      const w = homeWindow(interval, nowMs);
      return { from: Date.parse(w.startDate), to: Math.min(Date.parse(w.endDate), nowMs) };
    };
    const daysIn = async (): Promise<DayVisitors[]> => {
      const span = dayView ? { from: previous.from, to: current.to } : homeDays();
      return dayVisitors((await runAggregate(tenantId, visitorsByDayQuery(span), controller.signal)).rows);
    };
    const visitorsOn = (days: DayVisitors[], at: number): number => days.find((d) => d.at === at)?.visitors ?? 0;
    (async (): Promise<void> => {
      // Whether there is anything to count comes from the brand's addresses (cached per brand): not set up here, or no
      // tracked address, leaves the figure out; it is never read as 0 visitors.
      const hosts = await brandHosts(tenantId);
      if (!hosts || hosts.length === 0) {
        if (!live()) return;
        outFor.current = brand;
        setState({ brand, period: interval, status: 'unavailable', ...NONE });
        return;
      }
      outFor.current = undefined;
      // One read after the other (the service answers a burst with busy). The daily read only draws the chart line: a
      // failed one leaves the line out, never the figure.
      const now = await totalsIn(current);
      const before = await totalsIn(previous);
      const read = daily ? await daysIn().catch((): null => null) : null;
      const figures: Figures = {
        visitors: now,
        delta: Math.round(deltaPct(now, before)),
        days: read && dayView ? read.filter((d) => d.at >= current.from) : read,
        day:
          read && dayView ? { prev: visitorsOn(read, previous.from), current: visitorsOn(read, current.from) } : null,
        homeState: null,
        caption: null,
      };
      held.current.set(key, figures);
      if (live()) setState({ brand, period: interval, status: 'ready', ...figures });
    })().catch(failed);
    return () => controller.abort();
  }, [tenantId, interval, enabled, quality, brand]);

  const { brand: shownFor, period: shownPeriod, ...shown } = state;
  if (!reads) return { status: 'idle', ...NONE };
  // Another period's figure is never shown under this period's caption; a brand with nothing to count stays out across
  // periods.
  if (shownFor === brand && (shownPeriod === interval || shown.status === 'unavailable')) return shown;
  return { status: analyticsConfigured() ? 'loading' : 'unavailable', ...NONE };
};
